import { useMemo } from 'react';

import {
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  GiftDeliveryForStore,
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import {
  GiftOrderDeliveryMode,
  GiftOrderForStore,
  GiftOrderItem,
  GiftOrderItemType,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';

/**
 * 주문(order)에서 파생되는 쿠폰/배송 목록·배송모드 플래그·전달 통계를 계산하는 순수 훅.
 * useGiftOrderShare 에서 분리했다 — 부수효과 없이 order 에만 의존하므로 회귀 위험이 없다.
 */
export function useGiftOrderShareItems(order: GiftOrderForStore | null) {
  // === 쿠폰 목록 (취소/폐기된 쿠폰 제외) ===
  const couponList = useMemo(() => {
    return ((order?.coupons ?? []) as GiftCouponForStore[]).filter(
      (c) =>
        c.redeemStatus !== GiftCouponRedeemStatus.Canceled &&
        c.redeemStatus !== GiftCouponRedeemStatus.Disposed,
    );
  }, [order]);

  // === 배송형 상품 ===
  const deliveryItems = useMemo(() => {
    if (!order?.orderDetails) return [];
    return order.orderDetails.filter(
      (item) => item.type === GiftOrderItemType.Delivery,
    ) as GiftOrderItem<GiftItemForStore, Image>[];
  }, [order]);

  const deliveryMode = order?.deliveryMode;
  const isIndividualDelivery = deliveryMode === GiftOrderDeliveryMode.Individual;
  const isBulkDelivery = deliveryMode === GiftOrderDeliveryMode.Bulk;

  const deliveryList = useMemo(() => {
    return ((order?.deliveries ?? []) as GiftDeliveryForStore[]).filter(
      (d) => d.deliveryStatus !== GiftDeliveryStatus.Canceled,
    );
  }, [order]);

  // === 통계 (재발행/재발급 쿠폰 제외) ===
  const originalCouponList = couponList.filter((c) => !c.isAdditional && !c.isReissued);
  const totalCoupons = originalCouponList.length;
  const sharedCoupons = originalCouponList.filter(
    (c) => c.shareStatus !== GiftCouponShareStatus.NotShared,
  ).length;
  const unsharedCoupons = totalCoupons - sharedCoupons;

  const totalIndividualDeliveries = isIndividualDelivery ? deliveryList.length : 0;
  const sharedDeliveries = isIndividualDelivery
    ? deliveryList.filter((d) => d.shareStatus !== GiftDeliveryShareStatus.NotShared)
        .length
    : 0;
  const unsharedDeliveries = totalIndividualDeliveries - sharedDeliveries;

  const hasShareableItems = couponList.length > 0 || isIndividualDelivery;
  const totalUnshared = unsharedCoupons + unsharedDeliveries;

  return {
    couponList,
    deliveryItems,
    deliveryList,
    isIndividualDelivery,
    isBulkDelivery,
    totalCoupons,
    sharedCoupons,
    unsharedCoupons,
    totalIndividualDeliveries,
    sharedDeliveries,
    unsharedDeliveries,
    hasShareableItems,
    totalUnshared,
  };
}
